import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import AsyncSelect from 'react-select/async'
import { useTranslation } from 'react-i18next'

import API from '../../../../api/api'
import FormInput from '../FormInput'
import { mapClientToUser } from '../../../BuildingDashboard/Checklists/helpers'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import {
  selectClients,
  selectStatusIsLoadedAll,
  getClients,
} from '../../../../reducers/clientsReducer'
import MediaUpload from '../../../../components/MediaUpload/MediaUpload'
import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import { visitorTypes, getVisitorTypeOptions } from '../utils'
import { DIGIT_REGEX } from '../../../../helpers/constants'

import PermissionAccessBlock from './PermissionAccessBlock'
import Note from './Note'

const maxNumberOfFiles = 1
const ONE_MB = 1000000
const maxMBSize = 10
const maxFileSize = ONE_MB * maxMBSize // 10MB
const fileExtension = ['pdf']

const FirstStep = ({
  values,
  setFieldValue,
  inputLabelPrefix,
  selectedBuilding,
  users,
  isVendorModal,
  isEditMode,
  isRecuringType,
  handleChange,
}) => {
  const { t } = useTranslation(['visitors'])
  const loadResidentOptions = (inputValue, callback) => {
    setTimeout(async () => {
      const options = await API.getClients(0, 100, selectedBuilding.id, 0, inputValue)
      const filteredOptions = inputValue
        ? options.map((client) => ({
            ...client,
            value: client.id,
            label: `${client.user.firstName} ${client.user.lastName} #${client.apartment}`,
          }))
        : options
      callback(filteredOptions)
    }, 500)
  }

  const fetchClients = async (query = '', isScrolling = false, buildingId) => {
    if (!buildingId) return

    const limit = 15
    const offset =
      users.length % limit === 0
        ? users.length / limit
        : (users.length - (users.length % limit)) / limit

    await getClients(isScrolling ? offset + 1 : 1, buildingId, undefined, query, limit)
  }

  useEffect(() => {
    if (selectedBuilding) {
      fetchClients()
    }
  }, [selectedBuilding])

  const fieldName = t('visitors:form.fieldName')
  const fieldPhone = t('visitors:form.fieldPhone')
  const fieldEmail = t('visitors:form.fieldEmail')
  const fieldCompany = t('visitors:form.fieldCompany')
  const fieldId = t('visitors:form.fieldId')

  return (
    <React.Fragment>
      <div className="columns">
        <div className="column is-12">
          <FormInput label={t('visitors:form.selectResident')} name="resident">
            <AsyncSelect
              defaultOptions={users}
              placeholder={t('visitors:form.selectResidentPlaceholder')}
              name="resident"
              loadOptions={loadResidentOptions}
              value={values.resident}
              isDisabled={isEditMode}
              onChange={(selected) => {
                setFieldValue('clientId', selected.value)
                setFieldValue('resident', selected)
                setFieldValue('keyAccess', false)
                setFieldValue('pinCode', false)
              }}
            />
          </FormInput>
        </div>
      </div>
      <div className="columns">
        <div className="column is-12">
          <FormInput
            label={t('visitors:form.fieldLabel', { type: inputLabelPrefix, field: fieldName })}
            name="visitorName"
          >
            <TextField
              value={values.visitorName}
              name="visitorName"
              placeholder={t('visitors:form.enterFieldValue', {
                type: inputLabelPrefix.toLowerCase(),
                field: fieldName.toLowerCase(),
              })}
              type="text"
              onChange={handleChange}
            />
          </FormInput>
        </div>
      </div>
      {/* Note: Show only for non-recurring guest */}
      {!isRecuringType && !isVendorModal && (
        <div className="columns">
          <div className="column is-12">
            <FormInput
              label={t('visitors:form.selectVisitorTypeLabel')}
              name="visitorType"
              isOptional
            >
              <SelectField
                name="visitorType"
                value={values.visitorType.id}
                placeholder={t('visitors:form.selectVisitorType')}
                options={getVisitorTypeOptions(t)}
                onChange={(e) => {
                  const selectedValue = visitorTypes.find((type) => type.id === e.target.value)
                  setFieldValue('visitorType', {
                    id: selectedValue.id,
                    text: selectedValue.text,
                  })
                }}
                additionalClass="visitor-type-select"
              />
            </FormInput>
          </div>
        </div>
      )}
      <div className="columns">
        <div className="column is-6">
          <FormInput
            label={t('visitors:form.fieldLabel', { type: inputLabelPrefix, field: fieldPhone })}
            name="visitorPhone"
            isOptional={!values.keyAccess}
          >
            <TextField
              value={values.visitorPhone}
              name="visitorPhone"
              placeholder={t('visitors:form.enterFieldValue', {
                type: inputLabelPrefix.toLowerCase(),
                field: fieldPhone.toLowerCase(),
              })}
              type="text"
              onChange={handleChange}
            />
          </FormInput>
        </div>
        <div className="column is-6">
          <FormInput
            label={t('visitors:form.fieldLabel', { type: inputLabelPrefix, field: fieldEmail })}
            name="visitorEmail"
            isOptional={!values.keyAccess && !values.pinCode}
          >
            <TextField
              value={values.visitorEmail}
              name="visitorEmail"
              placeholder={t('visitors:form.enterFieldValue', {
                type: inputLabelPrefix.toLowerCase(),
                field: fieldEmail.toLowerCase(),
              })}
              type="email"
              onChange={handleChange}
            />
          </FormInput>
        </div>
      </div>
      <div className="columns">
        <div className="column is-6">
          <FormInput
            label={t('visitors:form.fieldLabel', { type: inputLabelPrefix, field: fieldCompany })}
            name="visitorCompany"
            isOptional
          >
            <TextField
              value={values.visitorCompany}
              name="visitorCompany"
              placeholder={t('visitors:form.enterFieldValue', {
                type: inputLabelPrefix.toLowerCase(),
                field: fieldCompany.toLowerCase(),
              })}
              type="text"
              onChange={handleChange}
            />
          </FormInput>
        </div>
        <div className="column is-6">
          <FormInput
            label={t('visitors:form.fieldLabel', { type: inputLabelPrefix, field: fieldId })}
            name="visitorId"
            isOptional
          >
            <TextField
              value={values.visitorId}
              name="visitorId"
              placeholder={t('visitors:form.enterFieldValue', {
                type: inputLabelPrefix.toLowerCase(),
                field: fieldId,
              })}
              type="text"
              onChange={(e) => {
                const numericValue = e.target.value.replace(DIGIT_REGEX, '')
                setFieldValue('visitorId', numericValue)
              }}
            />
          </FormInput>
        </div>
      </div>
      {isVendorModal && (
        <div className="columns">
          <div className="column is-12">
            <div className="new-visitor-form__upload-button">
              <p>{t('visitors:form.uploadCoi')}</p>
              <MediaUpload
                mediaTypes={fileExtension}
                files={values.coi ? [values.coi] : []}
                maxFiles={maxNumberOfFiles}
                maxSize={maxFileSize}
                onUpdate={(file) => setFieldValue('coi', file)}
              />
              <span>
                {t('visitors:form.coiFileInfo', {
                  formats: fileExtension.join(', ').toUpperCase(),
                  maxSize: maxMBSize,
                })}
              </span>
            </div>
          </div>
        </div>
      )}
      {!isRecuringType && (
        <div className="visitor-wrapper">
          <Note values={values} setFieldValue={setFieldValue} />
        </div>
      )}
      <PermissionAccessBlock
        isRecurring={isRecuringType}
        resident={values.resident}
        keyAccess={values.keyAccess}
        customAccess={values.customAccess}
        keylessAccess={values.keylessAccess}
        pinCode={values.pinCode}
        setFieldValue={setFieldValue}
      />
    </React.Fragment>
  )
}

FirstStep.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  isVendorModal: PropTypes.bool,
  isEditMode: PropTypes.bool,
  isRecuringType: PropTypes.bool,
  inputLabelPrefix: PropTypes.string,
  selectedBuilding: PropTypes.object,
  users: PropTypes.array,
}

const mapStateToProps = (state) => {
  const clients = selectClients(state) || []
  return {
    selectedBuilding: selectActiveBuilding(state),
    users: clients.map(mapClientToUser),
    allClientsLoaded: selectStatusIsLoadedAll(state),
  }
}

const mapDispatchToProps = {
  getClients,
}

export default connect(mapStateToProps, mapDispatchToProps)(FirstStep)
